import { Dialog, Transition } from '@headlessui/react';
import { ExclamationCircleIcon } from '@heroicons/react/outline';
import { ExchangeIcon, MailChimpIcon } from 'components/icons';
import { ButtonCTASmall, LoadingSpinner } from 'components/ui';
import querystring from 'querystring';
import { Fragment, useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router';
import { toast } from 'react-toastify';
import {
  useAddOAuthIntegrationMutation,
  useGetStoreInfoQuery,
} from 'store/services/segmentsApi';
import { useParseURLQueryParams } from 'utils';
import { LocationState } from '../types';

export const MailChimpConnect = () => {
  const { query } = useParseURLQueryParams();
  const history = useHistory();

  const segmentsClientID = process.env.REACT_APP_MAILCHIMP_CLIENT_ID;
  const redirectURI = process.env.REACT_APP_MAILCHIMP_REDIRECT_URI;

  const [addOAuthIntegration, { isLoading: isOAuthConfirmationLoading }] =
    useAddOAuthIntegrationMutation();

  const inputRef = useRef(null);

  const queryParams = querystring.stringify({
    store_name: query.store_name,
  });

  const { data: storeInfo } = useGetStoreInfoQuery(queryParams, {
    skip: !!!query.store_name,
  });

  const location = useLocation<LocationState>();

  const handleCloseModal = () => {
    if (location?.state?.background) {
      history.push(location.state.background);
    } else {
      history.replace({
        ...history.location,
        pathname: '/integrations/connect',
      });
    }
  };

  useEffect(() => {
    if (!!!query.code) return;

    let storeId = localStorage.getItem('mailchimp-integration');

    if (!!!storeId) return;

    addOAuthIntegration({
      provider: 'mailchimp',
      rawParams: {
        store_id: storeId,
        code: String(query.code),
      },
    })
      .unwrap()
      .then(() => {
        handleCloseModal();
        toast.success('Mailchimp connected successfuly');
        localStorage.removeItem('mailchimp-integration');
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.code]);

  // StoreId saved in the Local storage, once there is no way to identify which store the user authorized just from the Mailchimp OAuth return url
  const handleMailChimpLocalStorageSave = () => {
    localStorage.setItem('mailchimp-integration', String(storeInfo?.id));
  };

  return (
    <Transition show as={Fragment}>
      <Dialog
        as="div"
        id="modal"
        className="fixed inset-0 z-10 overflow-y-auto"
        static
        open
        initialFocus={inputRef}
        onClose={handleCloseModal}
      >
        <div className="min-h-screen text-center md:px-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100 "
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-30 backdrop-filter backdrop-blur-sm" />
          </Transition.Child>

          {/* This element is to trick the browser into centering the modal contents. */}
          <span
            className="inline-block h-screen align-middle"
            aria-hidden="true"
          >
            &#8203;
          </span>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <div className="inline-block w-full overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl md:h-segments-516px md:w-segments-648px">
              <div className="w-full h-48 px-5 pt-9 lg:px-12 bg-segments-accent-color-1 bg-opacity-5">
                <p className="text-black text-opacity-50 text-segments-13px">
                  Integrations
                </p>
                <div className="flex items-center mt-8 space-x-2 focus:outline-none">
                  <MailChimpIcon />
                  <ExchangeIcon />
                  <p>{storeInfo?.name}</p>
                </div>
                <p className="mt-4 text-2xl">Connect to MailChimp</p>
              </div>
              <div className="flex flex-col justify-between px-5 pt-8 text-gray-700 lg:px-12 h-80">
                {isOAuthConfirmationLoading ? (
                  <div
                    tabIndex={0}
                    className="flex justify-center mt-10 focus:outline-none"
                  >
                    <LoadingSpinner className="w-20 h-20 text-segments-accent-color-orange" />
                  </div>
                ) : (
                  <>
                    <div>
                      <p className="text-black text-opacity-50">
                        You will be redirected to MailChimp website for
                        authentication
                      </p>
                    </div>
                    <div className="flex flex-col items-center mt-6">
                      <a
                        className="`w-full text-base font-bold border focus:outline-none rounded-2xl bg-segments-accent-color-1 text-white hover:bg-segments-dark px-6 py-3"
                        href={`https://login.mailchimp.com/oauth2/authorize?response_type=code&client_id=${segmentsClientID}&redirect_uri=${redirectURI}`}
                        // target="_blank"
                        rel="noreferrer"
                        onClick={handleMailChimpLocalStorageSave}
                      >
                        Go to MailChimp
                      </a>

                      <div className="mt-6 text-segments-13px p-3 bg-[#FFECD6] rounded-xl w-full">
                        <div className="space-y-3 ">
                          <div className="flex space-x-3">
                            <ExclamationCircleIcon className="w-5 h-5" />
                            <p className="text-black text-opacity-60">
                              Syncing a segment might incurr in extra charge in
                              your Mailchimp plan.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex justify-between mt-8 mb-6 ">
                      <div>
                        <ButtonCTASmall
                          type="button"
                          variant="secondary"
                          onClick={handleCloseModal}
                        >
                          <p className="px-4 font-normal text-segments-13px">
                            Cancel
                          </p>
                        </ButtonCTASmall>
                      </div>
                      <div className="">
                        <ButtonCTASmall
                          variant="main"
                          type="button"
                          onClick={handleCloseModal}
                        >
                          <p className="w-20 text-segments-13px">Close</p>
                        </ButtonCTASmall>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
};
